import useAuthStore from '../store/authStore';

/**
 * Access control hook for frontend authorization.
 *
 * Returns functions to check effective capabilities (base role + grants).
 * Backend remains the authoritative enforcement point; frontend checks are UX-only.
 */
export function useEffectiveCapabilities() {
  const user = useAuthStore((s) => s.user);
  const grantCapabilities = useAuthStore((s) => s.grantCapabilities || []);
  const effectiveCapabilities = useAuthStore((s) => s.effectiveCapabilities || []);
  const cohortScopes = useAuthStore((s) => s.cohortScopes || []);
  const assessmentScopes = useAuthStore((s) => s.assessmentScopes || []);
  const placementScopes = useAuthStore((s) => s.placementScopes || []);

  /**
   * Check if the user has a specific capability.
   * Uses effective capabilities (base role + grants combined).
   *
   * @param {string} capability - Capability constant to check
   * @returns {boolean}
   */
  const hasCapability = (capability) => {
    if (!user || !capability) return false;
    // Use full effective capabilities (defaults + grants)
    return effectiveCapabilities.includes(capability);
  };

  /**
   * Get all effective capabilities for this user.
   * @returns {string[]}
   */
  const listCapabilities = () => {
    return effectiveCapabilities;
  };

  /**
   * Check if user has batch-scoped access for a specific batch.
   * Used for Cohort Faculty and other batch-scoped responsibilities.
   *
   * @param {string} batchId - Batch ObjectId as string
   * @returns {boolean}
   */
  const hasAccessToBatch = (batchId) => {
    if (!batchId || !Array.isArray(cohortScopes)) return false;
    return cohortScopes.some(
      (scope) =>
        (scope.scopeType === 'institution' && scope.relation === 'cohort_faculty') ||
        (scope.scopeType === 'batch' && String(scope.scopeId) === String(batchId))
    );
  };

  /**
   * Get all batches the user has scoped access to (via Cohort Faculty or similar).
   * @returns {string[]}
   */
  const getAccessibleBatches = () => {
    if (!Array.isArray(cohortScopes)) return [];
    return cohortScopes
      .filter((scope) => scope.scopeType === 'batch' && scope.scopeId)
      .map((scope) => String(scope.scopeId));
  };

  /**
   * Check if user has assessment-scoped access.
   * @param {string} [_assessmentId] - Optional assessment ObjectId for batch-level filtering
   * @returns {boolean}
   */
  const hasAssessmentAccess = (_assessmentId) => {
    if (!Array.isArray(assessmentScopes)) return false;
    // If any institution-scoped grant exists, all assessments accessible (backend validates)
    if (assessmentScopes.some((s) => s.scopeType === 'institution')) return true;
    // Batch-scoped assessment access: at least one scope must exist
    return assessmentScopes.length > 0;
  };

  /**
   * Check if user has placement-scoped access.
   * @param {string} [_driveId] - Optional mock drive ID for batch-level filtering
   * @returns {boolean}
   */
  const hasPlacementAccess = (_driveId) => {
    if (!Array.isArray(placementScopes)) return false;
    // If any institution-scoped grant exists, all placement ops accessible
    if (placementScopes.some((s) => s.scopeType === 'institution')) return true;
    // Batch-scoped placement access
    return placementScopes.length > 0;
  };

  return {
    user,
    hasCapability,
    listCapabilities,
    hasAccessToBatch,
    getAccessibleBatches,
    hasAssessmentAccess,
    hasPlacementAccess,
    // Capability arrays for direct inspection (advanced use)
    effectiveCapabilities,
    grantCapabilities,
    cohortScopes,
    assessmentScopes,
    placementScopes,
  };
}

export default useEffectiveCapabilities;
